import React from 'react';
import { Stack, Text } from '@fluentui/react';
import {
    SreStorageLocation,
    SreTransferInfo,
} from '../../../../../AGSS/models/SreHealth';
import { SreFact as Fact } from './SreFact';

interface ISreTransferFactsProps {
    info: SreTransferInfo | undefined;
}

const GUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

/**
 * Pulls the CTS flow id out of a configured flow string.
 *
 * Two formats are in use: `Flow: {guid}` in the sovereign clouds, and
 * `{src} --> Engine({guid}) -> {dst}` in development. Share-based (Core) routes contain no guid.
 */
export const flowIdOf = (flow?: string): string | undefined => {
    const match = flow?.match(GUID);
    return match && match[0].toLowerCase() !== EMPTY_GUID
        ? match[0]
        : undefined;
};

export const isPlaceholderFlow = (flow?: string): boolean =>
    Boolean(flow && flow.toLowerCase().includes(EMPTY_GUID));

const Locations: React.FC<{ title: string; locations: SreStorageLocation[] }> =
    ({ title, locations }) => (
        <Stack tokens={{ childrenGap: 12 }}>
            <Text as="h4" styles={{ root: { margin: 0, fontWeight: 600 } }}>
                {title}
            </Text>
            {locations.length === 0 && (
                <Text variant="small">Not recorded</Text>
            )}
            {locations.map((location) => (
                <Stack key={location.uri} tokens={{ childrenGap: 8 }}>
                    <Fact
                        label="Storage account"
                        value={location.storageAccount}
                        copyable
                    />
                    <Fact
                        label="Container"
                        value={location.storageContainer}
                        copyable
                    />
                    <Fact label="Location" value={location.uri} copyable />
                    <Fact
                        label="Source"
                        value={`${location.source}${
                            location.preferred ? ' (preferred)' : ''
                        }`}
                    />
                    {location.blobPrefix && (
                        <Fact label="Blob prefix" value={location.blobPrefix} />
                    )}
                </Stack>
            ))}
        </Stack>
    );

/**
 * Storage and transfer facts for one build.
 *
 * Rendered only when the API supplies them, so an older SRE deployment shows the panel unchanged
 * rather than a block of dashes.
 */
export const SreTransferFacts: React.FC<ISreTransferFactsProps> = ({
    info,
}) => {
    if (!info) {
        return null;
    }

    const { storageAccount, storageContainer, workLane, flowKey, flow } = info;
    const flowId = flowIdOf(info.flowId ?? undefined) ?? flowIdOf(flow);

    if (
        !storageAccount &&
        !storageContainer &&
        !workLane &&
        !flow &&
        !flowId &&
        !flowKey &&
        !info.transferLocations?.length &&
        !info.artifactLocations?.length
    ) {
        return null;
    }

    // Delos and GovSG carry an all-zero placeholder rather than a real flow. Showing it would read
    // as a working id, so treat it the same as no flow at all.
    const configured = Boolean(flowId || (flow && !isPlaceholderFlow(flow)));

    return (
        <Stack
            tokens={{ childrenGap: 8 }}
            role="region"
            aria-label="Storage and transfer"
        >
            <Text
                as="h3"
                variant="mediumPlus"
                styles={{ root: { margin: 0, fontWeight: 600 } }}
            >
                Storage and transfer
            </Text>
            <Fact label="Work lane" value={workLane} />
            {flowKey && <Fact label="Flow key" value={flowKey} />}
            <Fact
                label={flowId ? 'Flow id' : 'Flow'}
                value={configured ? flowId ?? flow : undefined}
                copyable={Boolean(flowId)}
                copyAs={flowId}
            />
            {flowId && flow && /Engine\(/i.test(flow) && (
                <Fact label="Route" value={flow} />
            )}
            {!configured && flowKey && (
                <Text variant="xSmall" styles={{ root: { opacity: 0.7 } }}>
                    No flow configured for {flowKey}.
                </Text>
            )}
            {info.transferLocations && (
                <Locations
                    title="Transfer candidates (not live-verified)"
                    locations={info.transferLocations}
                />
            )}
            {info.artifactLocations ? (
                <Locations
                    title="Recorded uploaded artifacts"
                    locations={info.artifactLocations}
                />
            ) : (
                (storageAccount || storageContainer) && (
                    <Stack tokens={{ childrenGap: 4 }}>
                        <Text
                            as="h4"
                            styles={{ root: { margin: 0, fontWeight: 600 } }}
                        >
                            Recorded uploaded artifact
                        </Text>
                        <Fact
                            label="Storage account"
                            value={storageAccount}
                            copyable
                        />
                        <Fact
                            label="Container"
                            value={storageContainer}
                            copyable
                        />
                    </Stack>
                )
            )}
        </Stack>
    );
};
